import { Injectable } from "@angular/core";
import { Router } from "@angular/router";
import { forkJoin, Observable, of, ReplaySubject, Subject } from "rxjs";
import { filter, switchMap, take, takeUntil } from "rxjs/operators";

import { BlockingUiSignalService } from "../blocking-ui-signal.service";
import { CoachMarkService } from "../coach-mark.service";
import { InteractionTrackingService, InteractionType } from "../interaction-tracking.service";
import { OnboardingStep, OnboardingStepService } from "../onboarding-step.service";
import { PaymentsService } from "../payments.service";
import { H01_LESSON_ID } from "./h01-home-introduction.service";
import { isOnRoute, whenUiQuiet } from "./onboarding-lesson-modal";
import { RS01_LESSON_ID, RS02_LESSON_ID } from "./rs-repeat-scoring.service";
import { S01_LESSON_ID, SCORE_LANDING_ROUTE } from "./s01-score-introduction.service";
import { ScoreContextualEntryService } from "./score-contextual-entry.service";

export const S01B_LESSON_ID = "S01B";

/** Recorded with S01B's completed lesson state - its CompletedAt *is* this milestone. */
export const SCORE_LANDING_START_CONTROL_INTRODUCED = "score_landing_start_control_introduced";

/** Anchors workbook v2.38: the landing page's Start a new scoring session control. */
export const S01B_ANCHOR = "score.start_new_session";

/** Copy rows S01B.01-S01B.02, workbook v2.38 Copy sheet (verbatim). */
export const S01B_COPY = {
  heading: "Start another Score",
  body: "When you want to check in again, select Start a new scoring session here.",
};

/**
 * Onboarding lesson S01B "Start another Score" (FLIP-367; spec v2.36 §15.1, workbook v2.38).
 * Informational anchored prompt (no buttons) at Start a new scoring session on the Score landing
 * page - it introduces the control to users who never saw S01 because their first Score started
 * straight from H01 or H02.
 *
 * - Eligible while the `onboarding-walkthrough` flag is on and S01B is unresolved, on a later
 *   landing visit after the first Score was submitted, when that Score started directly from H01/H02
 *   and S01 was suppressed. The direct-entry marker (ScoreLocalService.isOnboardingDirectEntry) does
 *   not survive a reload, so this is read from the durable rows instead: H01 resolved (so this is an
 *   onboarding-era account - existing users never replay a first-Score lesson) and S01 never
 *   presented (a suppressed S01 records nothing). Any failed check holds back.
 * - Second in the landing queue: after RS02, and never in an entry where RS01/RS02 appeared.
 * - Opening the control or the visible close (standard informational dismissal) completes S01B
 *   permanently. Leaving the page closes the prompt and records nothing.
 */
@Injectable({ providedIn: "root" })
export class S01BStartAnotherScoreService {
  private readonly cancel$ = new Subject<void>();
  private outcome$: ReplaySubject<boolean> | null = null;
  private promptOpen = false;
  /** Completed in this app session - its write may still be in flight. */
  private completed = false;

  constructor(
    private router: Router,
    private coachMarkService: CoachMarkService,
    private blockingUiSignal: BlockingUiSignalService,
    private paymentsService: PaymentsService,
    private onboardingStepService: OnboardingStepService,
    private entry: ScoreContextualEntryService,
    private interactionTracking: InteractionTrackingService
  ) {
    // Logout emits false - forget this account's state.
    this.paymentsService.subscriptionActive$.pipe(filter((active) => !active)).subscribe(() => {
      this.completed = false;
      this.leave();
    });
  }

  /**
   * Call on arrival at the Score landing page, once RS02 has passed. Emits once - true if the prompt
   * was presented, false if not. Never emits if the user leaves first.
   */
  maybeShow(): Observable<boolean> {
    this.cancelPending();
    const outcome$ = new ReplaySubject<boolean>(1);
    this.outcome$ = outcome$;

    this.entry
      .enabled$()
      .pipe(
        switchMap(() =>
          forkJoin([this.onboardingStepService.getSteps(), this.onboardingStepService.getSubmittedScoreCount()])
        ),
        switchMap(([steps, scoreCount]) => {
          const step = this.eligibleStep(steps, scoreCount);
          if (step === false) {
            return of(false as const);
          }
          return whenUiQuiet(this.blockingUiSignal.isBlockingUiActive$).pipe(
            switchMap(() => of(this.canShowNow() ? step : (false as const)))
          );
        }),
        take(1),
        takeUntil(this.cancel$)
      )
      .subscribe({
        next: (step) => {
          if (step !== false) {
            this.open(step);
          }
          this.settle(step !== false);
        },
        // The flag being off completes without a value - the queue still moves on.
        complete: () => this.settle(false),
      });

    return outcome$.pipe(take(1));
  }

  /** The real Start a new scoring session control - completes S01B while its prompt shows. */
  onStartNewSessionSelected(): void {
    if (!this.promptOpen) return;
    this.promptOpen = false;
    this.coachMarkService.dismiss();
    this.track("onboarding_lesson_action", "action: start_new_session");
    this.complete();
  }

  /** Call when leaving the Score landing page. Records nothing. */
  leave(): void {
    this.cancelPending();
    if (this.promptOpen) {
      this.promptOpen = false;
      this.coachMarkService.dismiss();
    }
  }

  private cancelPending(): void {
    const outcome$ = this.outcome$;
    this.outcome$ = null;
    outcome$?.complete();
    this.cancel$.next();
  }

  /** S01B's row (null if none yet) when eligible, or false. */
  private eligibleStep(steps: OnboardingStep[] | undefined, scoreCount: number | null): OnboardingStep | null | false {
    // Only definite answers qualify - "couldn't check" holds back rather than guessing.
    if (this.completed || steps === undefined || scoreCount === null || scoreCount < 1) {
      return false;
    }
    const step = steps.find((s) => s.stepKey === S01B_LESSON_ID) ?? null;
    if (step?.completedAt || step?.dismissed) {
      return false;
    }
    const h01 = steps.find((s) => s.stepKey === H01_LESSON_ID);
    const s01 = steps.find((s) => s.stepKey === S01_LESSON_ID);
    const firstScoreFromHome = !!(h01?.completedAt || h01?.dismissed);
    const s01Suppressed = !s01?.startedAt && !s01?.completedAt && !s01?.dismissed;
    return firstScoreFromHome && s01Suppressed ? step : false;
  }

  private canShowNow(): boolean {
    return (
      !this.completed &&
      isOnRoute(this.router, SCORE_LANDING_ROUTE) &&
      !this.entry.presentedThisEntry(RS01_LESSON_ID, RS02_LESSON_ID)
    );
  }

  private open(step: OnboardingStep | null): void {
    this.promptOpen = true;
    this.entry.markPresented(S01B_LESSON_ID);
    const now = new Date();
    this.onboardingStepService.upsertStep({
      stepKey: S01B_LESSON_ID,
      lastViewedAt: now,
      ...(step?.startedAt ? {} : { startedAt: now }),
    });

    // The engine tracks started / presented and the close for lessonId.
    this.coachMarkService.show({
      target: S01B_ANCHOR,
      heading: S01B_COPY.heading,
      message: S01B_COPY.body,
      lessonId: S01B_LESSON_ID,
      // Informational: the standard dismissal completes S01B.
      onDismiss: () => {
        this.promptOpen = false;
        this.complete();
      },
    });
  }

  private complete(): void {
    this.completed = true;
    this.onboardingStepService.upsertStep({ stepKey: S01B_LESSON_ID, completedAt: new Date() });
    this.track("onboarding_lesson_state_changed", `completed; milestone: ${SCORE_LANDING_START_CONTROL_INTRODUCED}`);
  }

  private settle(presented: boolean): void {
    this.outcome$?.next(presented);
    this.outcome$?.complete();
    this.outcome$ = null;
  }

  /** Same shape as CoachMarkService's FLIP-322 lesson events. */
  private track(eventName: string, detail?: string): void {
    this.interactionTracking.track({
      screen: this.router.url,
      eventName,
      interactionType: InteractionType.Selection,
      entityType: "OnboardingLesson",
      detail: detail ? `lesson: ${S01B_LESSON_ID}; ${detail}` : `lesson: ${S01B_LESSON_ID}`,
    });
  }
}
